import { useState } from "react";
import { hasRole, useApi, useApp } from "../../context/AppContext";
import { modelsOf } from "../../lib/fleet";

// The top of the Live & test tab (docs/tasks/35, phase 6): the cabinet's state
// from its health report rather than parsed out of the pipeline log, the door
// opened with a purpose, and the Pi 5's charging relay. The Test Console below
// keeps the live panes, the basket, recordings and the raw log.

function Chip({ label, value, tone }) {
  const cls = { good: "border-emerald-200 bg-emerald-50", bad: "border-rose-200 bg-rose-50", warn: "border-amber-200 bg-amber-50" }[tone]
    || "border-slate-200 bg-white";
  return (
    <div className={`rounded border px-3 py-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

export function LivePanel({ deviceId, health }) {
  const api = useApi();
  const { user } = useApp();
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const r = health?.report || {};
  const door = r.door || {};
  const m = modelsOf(health);
  const cams = r.cameras || {};
  const atCabinet = door.state === "open" || (r.session && r.session.state !== "closed");
  const isAdmin = hasRole(user, "tenant_admin");
  const isOperator = hasRole(user, "tenant_admin", "device_operator");

  const run = async (label, fn, confirmText, { needsIdle = true } = {}) => {
    if (needsIdle && atCabinet) { setMsg({ bad: true, text: "The door is open or a visit is running: wait until it ends." }); return; }
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true); setMsg(null);
    try {
      await fn();
      setMsg({ text: `${label}: sent. The cabinet picks commands up within 2 s; its answer appears in the commands list below.` });
    } catch (e) {
      setMsg({ bad: true, text: `${label}: ${e.message || e}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
        Live view and session recordings are a staff test facility. Frames of a purchase never leave a cabinet that serves customers:
        keep <code>session_recording_cloud_stream</code> off there (handbook ch. 7).
      </div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-6">
        <Chip label="Door" value={`${door.state || "—"}${door.locked ? " · locked" : ""}`} tone={door.state === "open" ? "warn" : undefined} />
        {door.layout === "pair" ? (
          <Chip label="Two locks" value={`${Object.entries(door.locks || {}).map(([k, v]) => `${k} ${v}`).join(" · ") || "—"}${door.relay_on ? " · coils on" : ""}`} />
        ) : (
          <Chip label="Lock" value={`${door.lock_backend || "—"} · ${door.signal === "sensor" ? "contact" : door.signal || "—"}`} />
        )}
        {door.charge && (
          <Chip label="Tablet charging" value={`${door.charge.on ? "on" : "off"}${door.charge.operator_off ? " (by operator)" : ""}${door.charge.relay_stuck ? " · relay stuck" : ""}`}
            tone={door.charge.relay_stuck ? "bad" : undefined} />
        )}
        <Chip label="Session" value={r.session ? `${r.session.state}` : "none"} tone={atCabinet ? "warn" : undefined} />
        {health?.service?.active && <Chip label="In service" value={`purchases refused${health.service.reason ? ` · ${health.service.reason}` : ""}`} tone="bad" />}
        <Chip label="Cameras" value={cams.expected != null ? `${cams.healthy}/${cams.expected}${cams.error ? " · error" : ""}` : "—"} tone={cams.error || cams.degraded || (cams.expected > 0 && cams.healthy < cams.expected) ? "bad" : "good"} />
        <Chip label="Recognition" value={`${r.analysis_mode || "—"}${r.review_window_mode ? ` · ${r.review_window_mode}` : ""}`} tone={r.recognition_enabled ? "good" : "bad"} />
        <Chip label="Encoder" value={m.encoder ? `${m.encoder.engine || "?"}${m.encoder.self_check != null ? ` · ${m.encoder.self_check}` : ""}` : "not reported"} />
        <Chip label="Detector" value={m.detector ? `${m.detector.name} · ${m.detector.engine || "?"}${m.detector.loaded === false ? " · not loaded" : ""}` : "none"}
          tone={m.detector?.loaded === false ? "bad" : undefined} />
      </div>
      {cams.error && <p className="text-xs text-rose-700">Cameras: {cams.error}</p>}

      {isOperator && (
        <div className="card">
          <h2 className="mb-2 text-sm font-semibold">At the cabinet</h2>
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-secondary text-xs" disabled={busy}
              onClick={() => run("Open for a test purchase", () => api.openCabinetDoor(deviceId, { purpose: "purchase", reason: "admin test purchase" }),
                "Unlock the door for a test purchase? Someone must be at the cabinet: what they take is reviewed and goes into a cart.")}>Open for a test purchase</button>
            <button className="btn btn-secondary text-xs" disabled={busy}
              onClick={() => run("Open for service", () => api.openCabinetDoor(deviceId, { purpose: "service", reason: "admin service opening" }),
                "Unlock the door for service? No review and no cart; the visit is recorded if recording is on.")}>Open for service</button>
            {door.charge && (
              <>
                <button className="btn btn-secondary text-xs" disabled={busy}
                  onClick={() => run("Read charging", () => api.sendDeviceCommand(deviceId, "charge.state"), null, { needsIdle: false })}>Read charging</button>
                {isAdmin && (
                  <>
                    <button className="btn btn-secondary text-xs" disabled={busy}
                      onClick={() => run("Charging self-test", () => api.sendDeviceCommand(deviceId, "charge.selftest"),
                        "Run the charging relay's self-test? It switches the tablet's charging off and on.")}>Charging self-test</button>
                    <button className="btn btn-secondary text-xs" disabled={busy}
                      onClick={() => run(door.charge.on ? "Charging off" : "Charging on",
                        () => api.sendDeviceCommand(deviceId, door.charge.on ? "charge.disable" : "charge.enable"),
                        door.charge.on ? "Switch the tablet's charging off? It stays off until someone switches it on again." : "Switch the tablet's charging back on?")}>
                      {door.charge.on ? "Switch charging off" : "Switch charging on"}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          {msg && <p className={`mt-2 text-xs ${msg.bad ? "text-rose-700" : "text-emerald-700"}`}>{msg.text}</p>}
        </div>
      )}
    </div>
  );
}
